import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';
import { locationService, LocationAnalytics } from '../services/locationService';

const LocationAnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<LocationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await locationService.getLocationAnalytics();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError('Failed to load location analytics');
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No analytics data available
      </Alert>
    );
  }

  const statCards = [
    {
      title: 'Total Locations',
      value: analytics.total_locations,
      icon: <LocationIcon sx={{ fontSize: 40 }} />,
      color: '#2E7D32',
    },
    {
      title: 'Average Rating',
      value: analytics.usage_statistics.average_rating.toFixed(1),
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      color: '#FF6F00',
    },
    {
      title: 'Total Reviews',
      value: analytics.usage_statistics.total_reviews,
      icon: <TrendingIcon sx={{ fontSize: 40 }} />,
      color: '#1976D2',
    },
    {
      title: 'Hunt Usage',
      value: analytics.usage_statistics.total_hunt_usage,
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      color: '#7B1FA2',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Location Analytics
      </Typography>

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Locations by Status
              </Typography>
              {Object.entries(analytics.locations_by_status).map(([status, count]) => (
                <Box key={status} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {status.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {count}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(count / analytics.total_locations) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Locations by Category
              </Typography>
              {Object.entries(analytics.locations_by_category)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 10)
                .map(([category, count]) => (
                <Box key={category} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {category.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {count}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(count / analytics.total_locations) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* External Service Coverage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                External Service Coverage
              </Typography>
              {Object.entries(analytics.external_service_coverage).map(([service, count]) => (
                <Box key={service} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {service.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {count} ({((count / analytics.total_locations) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(count / analytics.total_locations) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Submitters */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Location Submitters
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell align="right">Submissions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.top_submitters.slice(0, 10).map((submitter) => (
                      <TableRow key={submitter.user_id}>
                        <TableCell>{submitter.user_id}</TableCell>
                        <TableCell align="right">{submitter.submission_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Submission Trends */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Submission Trends (by Month)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                {Object.entries(analytics.submission_trends)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([month, count]) => (
                  <Box key={month} sx={{ mb: 2, minWidth: 200 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        {month}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(count / Math.max(...Object.values(analytics.submission_trends))) * 100}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Statistics Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {analytics.usage_statistics.total_reviews}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Reviews
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {analytics.usage_statistics.total_hunt_usage}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Hunt Usages
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {analytics.usage_statistics.average_rating.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average Rating
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {analytics.usage_statistics.locations_with_reviews}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Locations with Reviews
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LocationAnalyticsDashboard;